import { useSyncExternalStore } from 'react';
import { api, CLIENT_ID } from '../lib/api.ts';
import type { Block, Caret, Database, Doc, Page, Span } from './types.ts';

/**
 * One store for the whole workspace.
 *
 * Edits are applied here first and sent afterwards, so typing never waits for
 * a round trip — the database lives a long way from the browser and a 100ms
 * reply on every keystroke would be felt.
 */

export type State = {
  ready: boolean;
  error: string | null;
  signedIn: boolean;
  userId: string;
  workspace: { id: string; name: string; icon: string };
  me: { id: string; name: string; handle: string; color: string };
  pages: Record<string, Page>;
  recents: string[];
  trashCount: number;
  openId: string | null;
  doc: Doc | null;
  loadingDoc: boolean;
  expanded: Record<string, boolean>;
  caret: Caret | null;
  /** Blocks picked out as a unit, in document order. */
  selection: string[];
  /** The blocks currently being dragged, and where they would land. */
  dragIds: string[];
  dragOverId: string | null;
  dragWhere: 'above' | 'below' | null;
  saving: 'idle' | 'saving' | 'saved';
  finding: boolean;
  sidebarOpen: boolean;
};

const initial: State = {
  ready: false,
  error: null,
  signedIn: false,
  userId: '',
  workspace: { id: '', name: 'V.note', icon: '' },
  me: { id: '', name: '', handle: '', color: '#4a154b' },
  pages: {},
  recents: [],
  trashCount: 0,
  openId: null,
  doc: null,
  loadingDoc: false,
  expanded: {},
  caret: null,
  selection: [],
  dragIds: [],
  dragOverId: null,
  dragWhere: null,
  saving: 'idle',
  finding: false,
  sidebarOpen: true,
};

let state = initial;
const listeners = new Set<() => void>();

export function getState(): State { return state; }

export function setState(patch: Partial<State> | ((s: State) => Partial<State>)): void {
  const next = typeof patch === 'function' ? patch(state) : patch;
  state = { ...state, ...next };
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function useStore<T>(select: (s: State) => T): T {
  return useSyncExternalStore(subscribe, () => select(state), () => select(initial));
}

// ---- ids ------------------------------------------------------------------

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Matches the server's shape so an optimistic block keeps its id forever. */
export function newId(prefix: string, len = 12): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  let out = prefix;
  for (const byte of bytes) out += ALPHABET[byte % ALPHABET.length];
  return out;
}

// ---- loading --------------------------------------------------------------

export type Theme = 'light' | 'dark' | 'system';

export function applyTheme(theme: Theme): void {
  try { localStorage.setItem('vnote-theme', theme); } catch { /* private window */ }
  if (theme === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

export function currentTheme(): Theme {
  try {
    const saved = localStorage.getItem('vnote-theme');
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch { /* private window */ }
  return 'system';
}

export async function boot(): Promise<void> {
  try {
    const space = await api.get<any>('/api/space');
    // The sign-in screen pins dark; past it, the saved choice wins.
    applyTheme(currentTheme());
    const pages: Record<string, Page> = {};
    for (const p of space.pages) pages[p.id] = p;
    setState({
      ready: true,
      signedIn: true,
      userId: space.user_id,
      workspace: space.workspace,
      me: space.me,
      pages,
      recents: space.recents.map((r: any) => r.page_id),
      trashCount: space.trash_count,
    });
    connect(space.workspace.id);

    const wanted = new URL(location.href).searchParams.get('page');
    const first = wanted && pages[wanted] ? wanted
      : space.recents[0]?.page_id ?? Object.values(pages)
          .filter((p) => !p.parent_id)
          .sort((a, b) => a.position - b.position)[0]?.id;
    if (first) await openPage(first);
  } catch (err: any) {
    if (err?.status === 401) setState({ ready: true, signedIn: false });
    else setState({ ready: true, error: err?.message ?? 'Could not load the workspace.' });
  }
}

export async function openPage(id: string): Promise<void> {
  setState({ openId: id, loadingDoc: true });
  const url = new URL(location.href);
  url.searchParams.set('page', id);
  history.replaceState(null, '', url);
  try {
    const doc = await api.get<Doc>(`/api/pages/${id}`);
    // A second click while the first was in flight wins.
    if (getState().openId !== id) return;
    setState((s) => ({
      doc,
      loadingDoc: false,
      pages: { ...s.pages, [doc.page.id]: doc.page },
      recents: [id, ...s.recents.filter((r) => r !== id)].slice(0, 12),
    }));
  } catch (err: any) {
    setState({ loadingDoc: false, error: err?.message ?? 'That page could not be opened.' });
  }
}

export async function reloadDoc(): Promise<void> {
  const id = getState().openId;
  if (id) await openPage(id);
}

// ---- saving ---------------------------------------------------------------

let savingCount = 0;
let savedTimer: number | undefined;

/** Keeps one "Saving…"/"Saved" indicator honest across overlapping requests. */
async function tracked<T>(work: Promise<T>): Promise<T> {
  savingCount++;
  setState({ saving: 'saving' });
  try {
    return await work;
  } finally {
    savingCount--;
    if (savingCount === 0) {
      setState({ saving: 'saved' });
      window.clearTimeout(savedTimer);
      savedTimer = window.setTimeout(() => {
        if (savingCount === 0) setState({ saving: 'idle' });
      }, 1400);
    }
  }
}

/**
 * One queue out.
 *
 * Every change leaves in the order it was made. Without this, a block's text
 * and the Enter that split it are two requests in flight at once, and the one
 * that happens to land second wins — which is how a page ends up on screen one
 * way and in the database another.
 */
let outbound: Promise<unknown> = Promise.resolve();

function enqueue<T>(work: () => Promise<T>): Promise<T> {
  const next = outbound.then(work, work);
  // Keep the chain alive even when a step fails, or everything after it stalls.
  outbound = next.catch(() => undefined);
  return next;
}

/** Text edits are debounced per block; everything else is sent at once. */
const pendingText = new Map<string, Span[]>();
let textTimer: number | undefined;

export function saveBlockText(blockId: string, rich: Span[]): void {
  patchLocalBlock(blockId, { rich });
  pendingText.set(blockId, rich);
  window.clearTimeout(textTimer);
  textTimer = window.setTimeout(() => { void flushText(); }, 400);
}

export function flushText(): Promise<void> {
  window.clearTimeout(textTimer);
  if (!pendingText.size) return outbound.then(() => undefined, () => undefined);
  return enqueue(sendPendingText);
}

async function sendPendingText(): Promise<void> {
  if (!pendingText.size) return;
  const batch = [...pendingText.entries()];
  pendingText.clear();
  const pageId = getState().doc?.page.id;
  if (!pageId) return;
  await tracked(api.post('/api/blocks/batch', {
    page_id: pageId,
    ops: batch.map(([id, rich]) => ({ op: 'update', id, rich })),
  }).catch(() => {
    // Put it back so the next flush tries again rather than dropping an edit.
    for (const [id, rich] of batch) if (!pendingText.has(id)) pendingText.set(id, rich);
  }));
}

function patchLocalBlock(blockId: string, patch: Partial<Block>): void {
  setState((s) => {
    if (!s.doc) return {};
    return {
      doc: {
        ...s.doc,
        blocks: s.doc.blocks.map((b) => (b.id === blockId ? { ...b, ...patch } : b)),
      },
    };
  });
}

export function setLocalBlocks(blocks: Block[]): void {
  setState((s) => (s.doc ? { doc: { ...s.doc, blocks } } : {}));
}

/**
 * Rewrites the block list from whatever the store holds right now.
 *
 * Structural edits decide what to do from the list they were rendered with,
 * but they must apply to the live one — otherwise a fast second keystroke
 * writes back a list that never saw the first.
 */
export function applyBlocks(fn: (current: Block[]) => Block[]): void {
  setState((s) => (s.doc ? { doc: { ...s.doc, blocks: fn(s.doc.blocks) } } : {}));
}

/**
 * Patches one block against whatever the store holds right now, for the same
 * reason: two edits in one tick must not each map over the same stale array.
 */
export function patchBlock(id: string, patch: Partial<Block>): void {
  setState((s) => (s.doc
    ? { doc: { ...s.doc, blocks: s.doc.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)) } }
    : {}));
}

export type Op =
  | { op: 'insert'; id: string; type?: string; rich?: Span[]; props?: Record<string, any>; parent_id?: string | null; after?: string | null }
  | { op: 'update'; id: string; type?: string; rich?: Span[]; props?: Record<string, any>; parent_id?: string | null; after?: string | null; first?: boolean }
  | { op: 'delete'; id: string };

/** Sends structural changes; the local copy already shows them. */
export function sendOps(ops: Op[]): Promise<void> {
  if (!ops.length) return Promise.resolve();
  window.clearTimeout(textTimer);
  return enqueue(async () => {
    // Anything typed before this change goes first, so the server sees the
    // same sequence the person performed.
    await sendPendingText();

    const pageId = getState().doc?.page.id;
    if (!pageId) return;
    await tracked(
      api.post<{ blocks: Block[]; removed: string[] }>('/api/blocks/batch', { page_id: pageId, ops })
        .then((res) => {
          // Positions are the server's to decide; take them back so later
          // inserts land where they are meant to.
          setState((s) => {
            if (!s.doc || s.doc.page.id !== pageId) return {};
            const byId = new Map(res.blocks.map((b) => [b.id, b]));
            return {
              doc: {
                ...s.doc,
                blocks: s.doc.blocks.map((b) => {
                  const fresh = byId.get(b.id);
                  return fresh ? { ...b, position: fresh.position, parent_id: fresh.parent_id } : b;
                }),
              },
            };
          });
        })
        .catch(() => { void reloadDoc(); }),
    );
  });
}

export function setSelection(ids: string[]): void {
  const current = getState().selection;
  if (current.length === ids.length && current.every((id, i) => id === ids[i])) return;
  setState({ selection: ids });
}

export function clearSelection(): void {
  if (getState().selection.length) setState({ selection: [] });
}

export function setCaret(caret: Caret | null): void {
  setState({ caret });
}

// ---- pages ----------------------------------------------------------------

export async function createPage(opts: {
  parentId?: string | null; title?: string; isDatabase?: boolean; open?: boolean;
} = {}): Promise<Page | null> {
  try {
    const res = await tracked(api.post<{ page: Page; databases: Record<string, Database> }>('/api/pages', {
      parent_id: opts.parentId ?? null,
      title: opts.title ?? '',
      is_database: !!opts.isDatabase,
    }));
    setState((s) => ({
      pages: { ...s.pages, [res.page.id]: res.page },
      expanded: opts.parentId ? { ...s.expanded, [opts.parentId]: true } : s.expanded,
    }));
    if (opts.open !== false) await openPage(res.page.id);
    return res.page;
  } catch {
    setState({ error: 'Could not create that page.' });
    return null;
  }
}

export async function updatePage(id: string, patch: Record<string, unknown>): Promise<void> {
  setState((s) => {
    const page = s.pages[id];
    const next: Partial<State> = {};
    if (page) next.pages = { ...s.pages, [id]: { ...page, ...(patch as Partial<Page>) } };
    if (s.doc?.page.id === id) {
      next.doc = { ...s.doc, page: { ...s.doc.page, ...(patch as Partial<Page>) } };
    }
    return next;
  });
  await tracked(api.patch(`/api/pages/${id}`, patch).catch(() => { /* kept locally */ }));
}

export async function archivePage(id: string): Promise<void> {
  const { doc, pages } = getState();
  await tracked(api.del<{ archived: string[] }>(`/api/pages/${id}`).then((res) => {
    setState((s) => {
      const next = { ...s.pages };
      for (const gone of res.archived) delete next[gone];
      return { pages: next, trashCount: s.trashCount + res.archived.length };
    });
  }));
  if (doc?.page.id === id || (doc && doc.breadcrumb.some((c) => c.id === id))) {
    const fallback = Object.values(pages).find((p) => p.id !== id && !p.parent_id);
    if (fallback) await openPage(fallback.id);
    else setState({ doc: null, openId: null });
  }
}

export async function restorePage(id: string): Promise<void> {
  const res = await tracked(api.post<{ page: Page }>(`/api/pages/${id}/restore`));
  setState((s) => ({
    pages: { ...s.pages, [res.page.id]: res.page },
    trashCount: Math.max(0, s.trashCount - 1),
  }));
}

export async function duplicatePage(id: string): Promise<void> {
  const res = await tracked(api.post<{ page: Page }>(`/api/pages/${id}/duplicate`));
  setState((s) => ({ pages: { ...s.pages, [res.page.id]: res.page } }));
  await openPage(res.page.id);
}

export function toggleExpanded(id: string): void {
  setState((s) => ({ expanded: { ...s.expanded, [id]: !s.expanded[id] } }));
}

// ---- databases ------------------------------------------------------------

export function patchLocalDatabase(dbId: string, patch: Partial<Database>): void {
  setState((s) => {
    if (!s.doc?.databases[dbId]) return {};
    return {
      doc: {
        ...s.doc,
        databases: { ...s.doc.databases, [dbId]: { ...s.doc.databases[dbId]!, ...patch } },
      },
    };
  });
}

export async function refreshDatabase(dbId: string): Promise<void> {
  try {
    const db = await api.get<Database>(`/api/db/${dbId}`);
    patchLocalDatabase(dbId, db);
  } catch { /* the next page load will pick it up */ }
}

export async function addRow(dbId: string, props: Record<string, unknown> = {}): Promise<Page | null> {
  try {
    const row = await tracked(api.post<Page>(`/api/db/${dbId}/rows`, { props }));
    setState((s) => {
      const db = s.doc?.databases[dbId];
      if (!s.doc || !db) return {};
      return {
        doc: { ...s.doc, databases: { ...s.doc.databases, [dbId]: { ...db, rows: [...db.rows, row] } } },
      };
    });
    return row;
  } catch { return null; }
}

export async function setRowProps(dbId: string, rowId: string, props: Record<string, unknown>): Promise<void> {
  setState((s) => {
    const db = s.doc?.databases[dbId];
    if (!s.doc || !db) return {};
    return {
      doc: {
        ...s.doc,
        databases: {
          ...s.doc.databases,
          [dbId]: {
            ...db,
            rows: db.rows.map((r) => (r.id === rowId ? { ...r, props: { ...r.props, ...props } } : r)),
          },
        },
      },
    };
  });
  await tracked(api.patch(`/api/pages/${rowId}`, { props }).catch(() => { /* kept locally */ }));
}

export async function setRowTitle(dbId: string, rowId: string, title: string): Promise<void> {
  setState((s) => {
    const db = s.doc?.databases[dbId];
    if (!s.doc || !db) return {};
    return {
      doc: {
        ...s.doc,
        databases: {
          ...s.doc.databases,
          [dbId]: { ...db, rows: db.rows.map((r) => (r.id === rowId ? { ...r, title } : r)) },
        },
      },
    };
  });
  await tracked(api.patch(`/api/pages/${rowId}`, { title }).catch(() => { /* kept locally */ }));
}

export async function deleteRow(dbId: string, rowId: string): Promise<void> {
  setState((s) => {
    const db = s.doc?.databases[dbId];
    if (!s.doc || !db) return {};
    return {
      doc: {
        ...s.doc,
        databases: { ...s.doc.databases, [dbId]: { ...db, rows: db.rows.filter((r) => r.id !== rowId) } },
      },
    };
  });
  await tracked(api.del(`/api/pages/${rowId}`).catch(() => { /* kept locally */ }));
}

// ---- live updates from other devices --------------------------------------

let socket: WebSocket | null = null;
let retry = 0;

function connect(workspaceId: string): void {
  if (socket) return;
  const url = new URL('/ws', location.href);
  url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('workspace', workspaceId);

  const ws = new WebSocket(url);
  socket = ws;

  ws.onopen = () => { retry = 0; };
  ws.onclose = () => {
    socket = null;
    // Back off, but never give up: this tab may be open for days.
    const wait = Math.min(30_000, 1000 * 2 ** Math.min(retry++, 5));
    window.setTimeout(() => connect(workspaceId), wait);
  };
  ws.onmessage = (event) => {
    let msg: any;
    try { msg = JSON.parse(String(event.data)); } catch { return; }
    onServerEvent(msg);
  };
}

/** Another device changed something. Nothing here is trusted as an instruction. */
function onServerEvent(msg: { type?: string; [k: string]: any }): void {
  // Changes made in this tab come straight back; acting on them would reload
  // the page out from under whatever is being typed.
  if (msg.client_id && msg.client_id === CLIENT_ID) return;
  const s = getState();
  switch (msg.type) {
    case 'page_created':
    case 'page_updated':
      if (msg.page?.id) setState({ pages: { ...s.pages, [msg.page.id]: msg.page } });
      break;
    case 'pages_archived': {
      const next = { ...s.pages };
      for (const id of msg.ids ?? []) delete next[id];
      setState({ pages: next });
      break;
    }
    case 'blocks_changed':
    case 'block_saved':
    case 'block_removed':
    case 'page_reloaded':
      // Only reload when the change is to the page being looked at, and only
      // when nothing is being typed into it right now.
      if (msg.page_id && msg.page_id === s.openId && !document.querySelector('.rich:focus')) {
        void reloadDoc();
      }
      break;
    case 'db_changed':
      if (msg.db_id && s.doc?.databases[msg.db_id]) void refreshDatabase(msg.db_id);
      break;
  }
}

// ---- derived --------------------------------------------------------------

export function childrenOf(pages: Record<string, Page>, parentId: string | null): Page[] {
  return Object.values(pages)
    .filter((p) => p.parent_id === parentId && !p.db_id && !p.archived)
    .sort((a, b) => a.position - b.position || a.created_at - b.created_at);
}

export function blockTree(blocks: Block[], parentId: string | null = null): Block[] {
  return blocks
    .filter((b) => b.parent_id === parentId)
    .sort((a, b) => a.position - b.position);
}
